import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, Zap, MessageCircle, Video, Radio } from 'lucide-react';
import Logo, { LogoIcon } from './Logo';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signup } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password, name);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#030305]">
      {/* Ambient glow effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00F0FF] rounded-full blur-[180px] opacity-20" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FFE800] rounded-full blur-[180px] opacity-15" />
      </div>

      {/* Left panel - branding */}
      <div className="flex-1 hidden lg:flex items-center justify-center p-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-lg"
        >
          <div className="mb-8">
            <Logo size="lg" animate={true} />
          </div>
          <p className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#FF2A2A] via-[#FFE800] to-[#00F0FF]" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Your Social Universe
          </p>
          <p className="text-lg text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Chat, call, stream, and connect with people worldwide. Real-time translation breaks every barrier.
          </p>
          <div className="mt-8 flex gap-4">
            <div className="flex-1 bg-[#00F0FF]/10 border border-[#00F0FF]/20 rounded-2xl p-4 backdrop-blur-sm">
              <MessageCircle className="w-6 h-6 mb-2 text-[#00F0FF]" />
              <p className="font-semibold text-sm text-white">Smart Chat</p>
            </div>
            <div className="flex-1 bg-[#FFE800]/10 border border-[#FFE800]/20 rounded-2xl p-4 backdrop-blur-sm">
              <Video className="w-6 h-6 mb-2 text-[#FFE800]" />
              <p className="font-semibold text-sm text-white">HD Calls</p>
            </div>
            <div className="flex-1 bg-[#FF2A2A]/10 border border-[#FF2A2A]/20 rounded-2xl p-4 backdrop-blur-sm">
              <Radio className="w-6 h-6 mb-2 text-[#FF2A2A]" />
              <p className="font-semibold text-sm text-white">Go Live</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right panel - auth form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="bg-[#08080C] rounded-3xl p-8 border border-white/5 shadow-2xl shadow-black/50">
            {/* Mobile header */}
            <div className="lg:hidden mb-6 text-center">
              <div className="flex items-center justify-center mb-3">
                <Logo size="sm" animate={true} />
              </div>
              <p className="text-sm text-gray-500">Your Social Universe</p>
            </div>

            <div className="flex items-center gap-2 mb-6">
              <Zap className="w-5 h-5 text-[#00F0FF]" />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {isLogin ? 'Welcome Back' : 'Get Started'}
              </h2>
            </div>
            <p className="text-sm mb-6 text-gray-500">
              {isLogin ? 'Sign in to continue your journey' : 'Create your account to begin'}
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-sm font-medium text-red-400">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      data-testid="signup-name-input"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full pl-12 pr-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 transition-all"
                      placeholder="Enter your name"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    data-testid="auth-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 transition-all"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    data-testid="auth-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 transition-all"
                    placeholder="Enter your password"
                  />
                </div>
              </div>

              <motion.button
                data-testid="auth-submit-button"
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(0,240,255,0.3)' }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-3 rounded-full font-semibold text-black bg-[#00F0FF] shadow-lg shadow-[#00F0FF]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Please wait...
                  </span>
                ) : (
                  isLogin ? 'Sign In' : 'Sign Up'
                )}
              </motion.button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                <button
                  data-testid="auth-toggle-button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                  }}
                  className="ml-2 font-semibold text-[#00F0FF] hover:underline transition-all"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
              
              {/* Data collection disclosure for signup */}
              {!isLogin && (
                <p className="mt-4 text-xs text-gray-500 px-4">
                  By signing up, you agree to our{' '}
                  <a href="/terms" className="text-[#00F0FF] hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" className="text-[#00F0FF] hover:underline">Privacy Policy</a>.
                  We collect data to provide and improve our services.
                </p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;